/**
 * Data Stream store — raw MQTT inspector state.
 * Ingests RawMqttMessage into bounded per-topic ring buffers + a topic-stat
 * map (the topic tree is derived in useTopicTree). Pause halts writes (not
 * just animation); Clear wipes buffers/counters but retains the topic tree.
 */
import type { DetailTab, DsBadge, DsFilter } from '@/features/data-stream/types';
import { classifyTopic } from '@/features/data-stream/utils/classifyTopic';
import type { FieldFilter } from '@/features/data-stream/utils/payloadQuery';
import type { RawMqttMessage } from '@/lib/mqtt/types';
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';

/** Max retained messages per topic. */
export const RING_CAP = 200;

export interface TopicStat {
  topic: string;
  count: number;
  lastSeen: number;
  lastQos: 0 | 1 | 2;
  badge: DsBadge;
}

interface PaneWidths {
  tree: number;
  messages: number;
  detail: number;
}

interface DataStreamStore {
  topics: Map<string, TopicStat>;
  buffers: Map<string, RawMqttMessage[]>;
  selectedTopic: string | null;
  selectedMsgId: string | null;
  /** The pinned message object. Held independently of the ring buffer so a
   *  high-rate topic (which evicts the id within ~1s at RING_CAP) can't yank
   *  the inspected message — "pin & hold". */
  selectedMsg: RawMqttMessage | null;
  detailTab: DetailTab;
  filter: DsFilter;
  /** Topic-tree search — matches topic names only. Navigation, not inspection. */
  search: string;
  /** Free-text search over the *selected topic's* messages (column 2). */
  msgSearch: string;
  /** Field filters on the selected topic, built by clicking a value in the
   *  JSON pane or by picking a field from the discovered structure. ANDed
   *  together and with `msgSearch`. Cleared on topic change — they describe a
   *  shape that only the current topic is known to have. */
  fieldFilters: FieldFilter[];
  paused: boolean;
  /** True when `paused` was engaged automatically because the list is narrowed
   *  (search text or field-filter chips), rather than by the user hitting
   *  Pause. Only an auto-pause auto-resumes — an explicit Pause survives
   *  clearing the narrowing. */
  autoPaused: boolean;
  wildcardOn: boolean;
  totalMsgs: number;
  paneWidths: PaneWidths;

  ingest: (msg: RawMqttMessage) => void;
  /** Batch variant: applies many messages in a single store commit. The live
   *  feed (rAF-coalesced) uses this so a 250 msg/s topic produces ~60 commits/s
   *  (one per frame) instead of 250 — see useDataStreamFeed. */
  ingestBatch: (msgs: RawMqttMessage[]) => void;
  selectTopic: (topic: string | null) => void;
  selectMsg: (id: string | null) => void;
  setTab: (t: DetailTab) => void;
  setFilter: (f: DsFilter) => void;
  setSearch: (q: string) => void;
  setMsgSearch: (q: string) => void;
  addFieldFilter: (f: FieldFilter) => void;
  removeFieldFilter: (id: string) => void;
  clearFieldFilters: () => void;
  togglePause: () => void;
  clear: () => void;
  setWildcard: (on: boolean) => void;
  setPaneWidth: (pane: keyof PaneWidths, px: number) => void;
}

const DEFAULT_WIDTHS: PaneWidths = { tree: 240, messages: 280, detail: 380 };

/** Is the message list currently narrowed — by the search box, by field-filter
 *  chips (tapping a payload value), or both? */
function isNarrowing(msgSearch: string, fieldFilters: readonly FieldFilter[]): boolean {
  return msgSearch.trim().length > 0 || fieldFilters.length > 0;
}

/**
 * Pause/resume the feed to follow the narrowing state.
 *
 * Narrowing freezes the stream: a live topic pushes new rows in faster than a
 * match can be read, so the hit scrolls away mid-read. Dropping the last filter
 * resumes — but only when the pause was ours. An explicit Pause/Resume clears
 * `autoPaused`, which hands ownership back to the user and stops us from
 * overriding their choice. Returns a partial patch, empty when nothing changes.
 */
function autoPauseFor(
  s: { paused: boolean; autoPaused: boolean },
  narrowing: boolean,
): { paused?: boolean; autoPaused?: boolean } {
  if (narrowing && !s.paused) return { paused: true, autoPaused: true };
  if (!narrowing && s.autoPaused) return { paused: false, autoPaused: false };
  return {};
}

export const useDataStreamStore = create<DataStreamStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set) => ({
          topics: new Map(),
          buffers: new Map(),
          selectedTopic: null,
          selectedMsgId: null,
          selectedMsg: null,
          detailTab: 'json',
          filter: 'all',
          search: '',
          msgSearch: '',
          fieldFilters: [],
          paused: false,
          autoPaused: false,
          wildcardOn: false,
          totalMsgs: 0,
          paneWidths: DEFAULT_WIDTHS,

          ingest: (msg) =>
            set((s) => {
              if (s.paused) return s;
              // topic stat (new Map ref so selectors using it re-run)
              const topics = new Map(s.topics);
              const prev = topics.get(msg.topic);
              topics.set(msg.topic, {
                topic: msg.topic,
                count: (prev?.count ?? 0) + 1,
                lastSeen: msg.receivedAt,
                lastQos: msg.qos,
                badge: prev?.badge ?? classifyTopic(msg.topic),
              });
              // ring buffer (newest first, capped)
              const buffers = new Map(s.buffers);
              const cur = buffers.get(msg.topic) ?? [];
              const next = [msg, ...cur];
              if (next.length > RING_CAP) next.length = RING_CAP;
              buffers.set(msg.topic, next);
              return { topics, buffers, totalMsgs: s.totalMsgs + 1 };
            }),

          ingestBatch: (msgs) =>
            set((s) => {
              if (s.paused || msgs.length === 0) return s;
              const topics = new Map(s.topics);
              const buffers = new Map(s.buffers);
              for (const msg of msgs) {
                const prev = topics.get(msg.topic);
                topics.set(msg.topic, {
                  topic: msg.topic,
                  count: (prev?.count ?? 0) + 1,
                  lastSeen: msg.receivedAt,
                  lastQos: msg.qos,
                  badge: prev?.badge ?? classifyTopic(msg.topic),
                });
                const cur = buffers.get(msg.topic) ?? [];
                const next = [msg, ...cur];
                if (next.length > RING_CAP) next.length = RING_CAP;
                buffers.set(msg.topic, next);
              }
              return { topics, buffers, totalMsgs: s.totalMsgs + msgs.length };
            }),

          // Message-level search + field filters are scoped to the topic they
          // were built against, so they reset with the selection rather than
          // silently hiding everything on the next topic.
          selectTopic: (topic) =>
            set((s) => ({
              selectedTopic: topic,
              selectedMsgId: null,
              selectedMsg: null,
              msgSearch: '',
              fieldFilters: [],
              // Dropping the narrowing here has to resume the feed too, else
              // the stream stays frozen with no search or chip left to say why.
              ...autoPauseFor(s, false),
            })),
          // Resolve + pin the message object so eviction from the ring can't
          // change what's inspected/plotted. id === null clears the pin.
          selectMsg: (id) =>
            set((s) => {
              if (id == null) return { selectedMsgId: null, selectedMsg: null };
              const buf = s.selectedTopic ? s.buffers.get(s.selectedTopic) : undefined;
              return { selectedMsgId: id, selectedMsg: buf?.find((m) => m.id === id) ?? null };
            }),
          setTab: (t) => set({ detailTab: t }),
          setFilter: (f) => set({ filter: f }),
          setSearch: (q) => set({ search: q }),
          setMsgSearch: (q) =>
            set((s) => ({
              msgSearch: q,
              ...autoPauseFor(s, isNarrowing(q, s.fieldFilters)),
            })),
          // Re-clicking the same value is a no-op rather than a duplicate chip.
          addFieldFilter: (f) =>
            set((s) => {
              if (s.fieldFilters.some((x) => x.id === f.id)) return s;
              const fieldFilters = [...s.fieldFilters, f];
              return { fieldFilters, ...autoPauseFor(s, isNarrowing(s.msgSearch, fieldFilters)) };
            }),
          removeFieldFilter: (id) =>
            set((s) => {
              const fieldFilters = s.fieldFilters.filter((f) => f.id !== id);
              return { fieldFilters, ...autoPauseFor(s, isNarrowing(s.msgSearch, fieldFilters)) };
            }),
          clearFieldFilters: () =>
            set((s) => ({ fieldFilters: [], ...autoPauseFor(s, isNarrowing(s.msgSearch, [])) })),
          // An explicit Pause/Resume takes ownership of the pause state, so a
          // later search-clear won't override what the user just chose.
          togglePause: () => set((s) => ({ paused: !s.paused, autoPaused: false })),
          clear: () =>
            set({ buffers: new Map(), totalMsgs: 0, selectedMsgId: null, selectedMsg: null }),
          setWildcard: (on) => set({ wildcardOn: on }),
          setPaneWidth: (pane, px) => set((s) => ({ paneWidths: { ...s.paneWidths, [pane]: px } })),
        }),
        {
          name: 'flysafe-v2-data-stream',
          // Only UI prefs persist; live data is ephemeral.
          partialize: (s) => ({
            filter: s.filter,
            wildcardOn: s.wildcardOn,
            paneWidths: s.paneWidths,
          }),
        },
      ),
    ),
    { name: 'dataStream' },
  ),
);
